<template>
  <div v-if="isReady" class="content">
    <template v-for="displayField in getDisplayFields">
      <div
        class="field"
        v-if="isNotEmpty(fieldData[displayField.name])"
        :key="displayField.name"
      >
        <div class="field-label field__label">
          {{ displayField.label }}
        </div>
        <div
          v-if="vHtmlIsSet(displayField, fieldData)"
          v-html="displayField.formatter(fieldData[displayField.name]).v_html"
          class="field-item field__item"
          :data-qa="displayField.name"
        />
        <div
          v-else-if="displayField.type === 'link'"
          class="field-item field__item"
          :data-qa="displayField.name"
        >
          <b-link
            v-if="fieldData[displayField.name].url"
            :href="fieldData[displayField.name].url"
          >
            {{ displayField.formatter(fieldData[displayField.name].text) }}
          </b-link>
          <span v-else>{{
            displayField.formatter(fieldData[displayField.name].text)
          }}</span>
        </div>
        <div v-else-if="displayField.type === 'file_listing'">
          <file-attachment-listing
            :key="displayField.name + '-file-listing'"
            :files="fieldData[displayField.name]"
            allow-file-download
            :allow-file-remove="false"
          />
        </div>
        <div v-else class="field-item field__item" :data-qa="displayField.name">
          {{ displayField.formatter(fieldData[displayField.name]) }}
        </div>
      </div>
    </template>
    <div id="incident-report-view-controls" v-if="displayInvestigateButton">
      <b-button type="button" @click="gotoInvestigation()">
        Investigate
      </b-button>
    </div>
  </div>
</template>

<script>
import Jsona from "jsona";
import EntityViewDisplayFieldFormatter from "../../lib/entity_view_display_field_formatters";
import FileAttachmentListing from "../FileAttachmentListing.vue";
import { jsonClient, restClient } from "../../lib/raftApi";
import raftGenericHelper from "../../lib/raft_generic_helper";

const dataFormatter = new Jsona();

export default {
  name: "IncidentReportView",
  inject: ["isB4"],
  components: {
    FileAttachmentListing,
  },
  props: {
    reportUuid: {
      type: String,
      required: true,
    },
    showInvestigateButton: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data() {
    return {
      defaultQueries: {
        include:
          "uid,field_in_report_building,field_in_report_campus,field_in_report_space_room,field_in_report_upload_photos",
      },
      response: null,
      displayData: null,
      fieldData: null,
      investigation: null,
      perms: [],
      labelMap: {
        field_inc_report_incident_date: "Date Occurred:",
        field_inc_report_incident_time: "Time",
        created: "Date Submitted:",
        uid: "Submitted By:",
        field_in_report_building: "Building:",
        field_in_report_space_room: "Space/Room:",
        field_in_report_loc_specifics: "Location Specifics:",
        field_in_report_type_of_report: "Report Type:",
        field_in_report_description: "Description:",
        field_inc_report_witnesses: "Names of Witnesses (if any):",
        field_inc_report_people_involved: "Names of Parties Involved:",
        field_inc_report_supervisor_name: "Supervisor Name:",
        field_inc_report_med_treatment: "Medical treatment initially utilized:",
        field_inc_report_reporter_phone: "Reporter Phone Number:",
        field_in_report_upload_photos: "Photos",
      },
      typeMap: {
        uid: "link",
        field_in_report_upload_photos: "file_listing",
        created: "string",
      },
    };
  },
  computed: {
    getDisplayFields: function () {
      const displayFields = [];
      Object.entries(this.displayData).forEach((display) => {
        const [fieldName, data] = display;
        const field = { name: fieldName, label: data.label };
        // Allow remapping of incorrect JSON types from typeMap if necessary.
        data.display_settings.type =
          this.typeMap[fieldName] || data.display_settings.type;

        // Map labels.
        field.label = this.labelMap[fieldName] || field.label;
        // Add types to fields to more easily use conditionals in the templates.
        field.type = data.display_settings.type;
        field.formatter = (value) =>
          EntityViewDisplayFieldFormatter.fieldFormatter(value, data);
        displayFields.push(field);
      });
      return displayFields;
    },
    isReady: function () {
      return (
        this.isNotEmpty(this.displayData) && this.isNotEmpty(this.response)
      );
    },
    displayInvestigateButton: function () {
      return (
        this.showInvestigateButton &&
        this.investigation !== null &&
        this.investigation.user_edit_access
      );
    },
  },
  created() {},
  async mounted() {
    const options = {
      path: "entity-view-display/node/incident_report",
      queries: {
        display_id: "default",
      },
    };
    const response = await restClient.get(options).catch((e) => {
      this.errorEmit(e);
    });
    const data = response.incident_report;
    this.displayData = data.default;
    this.materialTypeLabel = data["bundle-label"];

    const jsonApiOptions = {
      path: "node/incident_report/" + this.reportUuid,
      queries: this.defaultQueries,
    };
    const jsonApiResponse = await jsonClient
      .get(jsonApiOptions)
      .catch((e) => this.errorEmit(e));
    this.buildFieldData(jsonApiResponse);
    this.response = jsonApiResponse;

    const investigationApiOptions = {
      path: "node/incident_investigation",
      queries: {
        "filter[field_incident_report.id]": this.reportUuid,
        "fields[node--incident_investigation]": "title,user_edit_access",
      },
    };
    const investigationResponse = await jsonClient
      .get(investigationApiOptions)
      .catch((e) => this.errorEmit(e));
    this.investigation = investigationResponse.deserialized[0];
  },
  methods: {
    isNotEmpty: function (obj) {
      // Handles object and array.
      if (obj && typeof obj === "object") {
        return Object.keys(obj).length > 0;
      }
      if (typeof obj === "boolean" || typeof obj === "number") {
        return true;
      }
      return !!obj;
    },
    buildFieldData: function (response) {
      this.fieldData = {};
      Object.entries(response.data.attributes).forEach((field) => {
        const [name, value] = field;
        this.fieldData[name] = value;
      });
      // Handle title.
      raftGenericHelper.setPageTitle(this.fieldData.title);
      delete this.fieldData.title;
      // Handle relations.
      const flattened = dataFormatter.deserialize(response);
      const relationships = [
        "field_in_report_campus",
        "field_in_report_building",
        "field_in_report_space_room",
        "field_in_report_upload_photos",
        "uid",
      ];
      relationships.forEach((key) => {
        if (flattened.hasOwnProperty(key)) {
          // @todo Unify with code in EntityViewDisplayFieldFormatters.formatEntityReferenceLabel().
          if (
            Array.isArray(flattened[key]) &&
            flattened[key].length &&
            flattened[key][0].hasOwnProperty("name")
          ) {
            this.fieldData[key] = flattened[key][0].name;
          } else if (flattened[key]) {
            if (flattened[key].hasOwnProperty("name")) {
              this.fieldData[key] = flattened[key].name;
            }
            if (flattened[key].hasOwnProperty("title")) {
              this.fieldData[key] = flattened[key].title;
            }
          }
        }
      });

      // Fix created date format.
      const createdDate = new Date(this.fieldData.created);
      // Format with time added.
      this.fieldData.created = raftGenericHelper.formatDateTime(createdDate);

      // Handle author
      let user = "Anonymous";
      let url = null;
      if (!flattened.field_in_report_anon_submission) {
        if (flattened.uid.drupal_internal__uid === 1) {
          user = "Admin";
        } else {
          user = flattened.uid.field_prof_display_name
            ? flattened.uid.field_prof_display_name
            : flattened.uid.display_name;
          url = "/user/" + flattened.uid.drupal_internal__uid;
        }
      }
      this.fieldData.uid = { text: user, url: url };

      // Handle file uploads.
      this.fieldData.field_in_report_upload_photos =
        flattened.field_in_report_upload_photos;
    },
    gotoInvestigation() {
      window.location.href =
        "/raft/incident-management/incident-investigation/" +
        this.investigation.id +
        "/edit";
    },
    errorEmit(message, data) {
      this.$emit("form-error-message", { message: message, data: data });
    },
    vHtmlIsSet(displayField, fieldData) {
      return !!(
        displayField.formatter(fieldData[displayField.name]) &&
        displayField
          .formatter(fieldData[displayField.name])
          .hasOwnProperty("v_html") &&
        typeof displayField.formatter(fieldData[displayField.name]).v_html !==
          "undefined"
      );
    },
  },
};
</script>

<style scoped>
div.field {
  display: block;
  clear: both;
}

.field-label.field__label {
  width: 200px;
  float: left;
  display: inline-block;
  vertical-align: top;
}

.field-item.field__item {
  display: table-cell;
  vertical-align: top;
}

.incident-report-location-field :deep(select#building-fil) {
  max-width: 300px;
}

.incident-report-location-field :deep(select#space-fil) {
  max-width: 300px;
}
</style>
